<template>
  <div id="app" :class="{ 'is-mobile': isMobile }">
    <div v-if="authLoading" class="auth-loading" v-loading="true" element-loading-text="正在验证登录状态..." />

    <Login v-else-if="!user" @success="onLoginSuccess" />

    <MobileLayout
      v-else-if="isMobile"
      :user="user"
      :active-menu="mobileActiveMenu"
      :page-title="pageTitle"
      :show-back="activeMenu !== 'home'"
      @select="onMenuSelect"
      @back="onMobileBack"
      @logout="handleLogout"
    >
      <MobileHome
        v-if="loadedPages.home"
        v-show="activeMenu === 'home'"
        @navigate="onMenuSelect"
      />
      <MobileRecommend
        v-if="loadedPages['system-recommend']"
        v-show="activeMenu === 'system-recommend'"
      />
      <MobileRadar
        v-if="loadedPages['window-ranking']"
        v-show="activeMenu === 'window-ranking'"
      />
      <BacktestView
        v-if="loadedPages.backtest"
        v-show="activeMenu === 'backtest'"
        :visible="activeMenu === 'backtest'"
      />
      <BacktestForecast
        v-if="loadedPages['backtest-forecast']"
        v-show="activeMenu === 'backtest-forecast'"
      />
      <MobileKline
        v-if="loadedPages.kline"
        v-show="activeMenu === 'kline'"
      />
      <MobileFund
        v-if="loadedPages.fund"
        v-show="activeMenu === 'fund'"
      />
      <MobileMore
        v-if="loadedPages.more"
        v-show="activeMenu === 'more'"
        @navigate="onMenuSelect"
        @logout="handleLogout"
      />
      <!-- 模拟实盘：移动端原生实现（非 PC 页降级入口），故不放进 m-extra-wrap -->
      <MobileSimTrading
        v-if="loadedPages['sim-trading']"
        v-show="activeMenu === 'sim-trading'"
        :visible="activeMenu === 'sim-trading'"
      />
      <!-- 神奇九转：移动端原生实现（PC 宽表在手机上不可用），同样不放 m-extra-wrap -->
      <MobileTD9
        v-if="loadedPages.td9"
        v-show="activeMenu === 'td9'"
      />
      <!-- 趋势为王：移动端原生实现（PC 端 13 列宽表在手机上不可用），同样不放 m-extra-wrap -->
      <MobileTrendKing
        v-if="loadedPages['trend-king']"
        v-show="activeMenu === 'trend-king'"
      />
      <!-- 年线偏离：移动端原生实现（PC 端 14 列宽表在手机上不可用），同样不放 m-extra-wrap -->
      <MobileMaDeviation
        v-if="loadedPages['ma-deviation']"
        v-show="activeMenu === 'ma-deviation'"
      />
      <div v-if="isMobileExtraPage" class="m-extra-wrap">
        <van-notice-bar text="该页移动端为简化入口，复杂操作建议使用电脑版" />
        <IndustryMarket
          v-if="loadedPages.industry"
          v-show="activeMenu === 'industry'"
          :visible="activeMenu === 'industry'"
        />
        <IndustryTrend
          v-if="loadedPages['industry-trend']"
          v-show="activeMenu === 'industry-trend'"
          :visible="activeMenu === 'industry-trend'"
        />
        <StockTrend
          v-if="loadedPages['stock-trend']"
          v-show="activeMenu === 'stock-trend'"
          :visible="activeMenu === 'stock-trend'"
        />
        <IndexTrend
          v-if="loadedPages['index-trend']"
          v-show="activeMenu === 'index-trend'"
          :visible="activeMenu === 'index-trend'"
        />
        <FundIndustry
          v-if="loadedPages['fund-industry']"
          v-show="activeMenu === 'fund-industry'"
          :visible="activeMenu === 'fund-industry'"
        />
        <FundIndustryDetail
          v-if="loadedPages['fund-industry-detail']"
          v-show="activeMenu === 'fund-industry-detail'"
          :visible="activeMenu === 'fund-industry-detail'"
          :is-mobile="true"
          :period="routeQuery.period || ''"
          :code="routeQuery.code || ''"
        />
      </div>
    </MobileLayout>

    <DesktopLayout
      v-else
      :user="user"
      :active-menu="activeMenu"
      :page-title="pageTitle"
      :is-admin="isAdmin"
      @select="onMenuSelect"
      @logout="handleLogout"
    >
      <HomePage
        v-if="loadedPages.home"
        v-show="activeMenu === 'home'"
        :visible="activeMenu === 'home'"
        :is-admin="isAdmin"
        key="home"
        @navigate="onMenuSelect"
      />
      <FundHoldings
        v-if="loadedPages.fund"
        v-show="activeMenu === 'fund'"
        :visible="activeMenu === 'fund'"
        key="fund"
      />
      <FundIndustry
        v-if="loadedPages['fund-industry']"
        v-show="activeMenu === 'fund-industry'"
        :visible="activeMenu === 'fund-industry'"
        key="fund-industry"
      />
      <FundIndustryDetail
        v-if="loadedPages['fund-industry-detail']"
        v-show="activeMenu === 'fund-industry-detail'"
        :visible="activeMenu === 'fund-industry-detail'"
        :period="routeQuery.period || ''"
        :code="routeQuery.code || ''"
        key="fund-industry-detail"
      />
      <KlineQuery
        v-if="loadedPages.kline"
        v-show="activeMenu === 'kline'"
        :visible="activeMenu === 'kline'"
        key="kline"
      />
      <SystemRecommend
        v-if="loadedPages['system-recommend']"
        v-show="activeMenu === 'system-recommend'"
        key="system-recommend"
      />
      <BacktestView
        v-if="loadedPages.backtest"
        v-show="activeMenu === 'backtest'"
        :visible="activeMenu === 'backtest'"
        key="backtest"
      />
      <BacktestForecast
        v-if="loadedPages['backtest-forecast']"
        v-show="activeMenu === 'backtest-forecast'"
        key="backtest-forecast"
      />
      <TrendKing
        v-if="loadedPages['trend-king']"
        v-show="activeMenu === 'trend-king'"
        key="trend-king"
      />
      <MagicNineTurn
        v-if="loadedPages.td9"
        v-show="activeMenu === 'td9'"
        :visible="activeMenu === 'td9'"
        key="td9"
      />
      <!-- 年线偏离：宽表（14 列），桌面端专页，移动端不挂（与神奇九转/趋势为王一致的处理） -->
      <MaDeviation
        v-if="loadedPages['ma-deviation']"
        v-show="activeMenu === 'ma-deviation'"
        :visible="activeMenu === 'ma-deviation'"
        key="ma-deviation"
      />
      <WindowRanking
        v-if="loadedPages['window-ranking']"
        v-show="activeMenu === 'window-ranking'"
        key="window-ranking"
      />
      <IndustryMarket
        v-if="loadedPages.industry"
        v-show="activeMenu === 'industry'"
        :visible="activeMenu === 'industry'"
        key="industry"
      />
      <IndustryTrend
        v-if="loadedPages['industry-trend']"
        v-show="activeMenu === 'industry-trend'"
        :visible="activeMenu === 'industry-trend'"
        key="industry-trend"
      />
      <StockTrend
        v-if="loadedPages['stock-trend']"
        v-show="activeMenu === 'stock-trend'"
        :visible="activeMenu === 'stock-trend'"
        key="stock-trend"
      />
      <IndexTrend
        v-if="loadedPages['index-trend']"
        v-show="activeMenu === 'index-trend'"
        :visible="activeMenu === 'index-trend'"
        key="index-trend"
      />
      <SimTrading
        v-if="loadedPages['sim-trading']"
        v-show="activeMenu === 'sim-trading'"
        :visible="activeMenu === 'sim-trading'"
        :is-admin="isAdmin"
        key="sim-trading"
      />
      <ThemeTagging
        v-if="loadedPages['theme-tagging'] && isAdmin"
        v-show="activeMenu === 'theme-tagging'"
        key="theme-tagging"
      />
      <ThemeDict
        v-if="loadedPages['theme-dict'] && isAdmin"
        v-show="activeMenu === 'theme-dict'"
        key="theme-dict"
      />
      <DataConfig
        v-if="loadedPages['data-config']"
        v-show="activeMenu === 'data-config'"
        key="data-config"
      />
      <UserManagement
        v-if="loadedPages.users && isAdmin"
        v-show="activeMenu === 'users'"
        key="users"
      />
      <LoginHistory
        v-if="loadedPages['login-logs'] && isAdmin"
        v-show="activeMenu === 'login-logs'"
        key="login-logs"
      />
      <SyncLog
        v-if="loadedPages['sync-logs'] && isAdmin"
        v-show="activeMenu === 'sync-logs'"
        key="sync-logs"
      />
    </DesktopLayout>
  </div>
</template>

<script>
import HomePage from './components/Home.vue'
import FundHoldings from './components/FundHoldings.vue'
import FundIndustry from './components/FundIndustry.vue'
import FundIndustryDetail from './components/FundIndustryDetail.vue'
import KlineQuery from './components/KlineQuery.vue'
import SystemRecommend from './components/SystemRecommend.vue'
import BacktestView from './components/BacktestView.vue'
import BacktestForecast from './components/BacktestForecast.vue'
import MagicNineTurn from './components/MagicNineTurn.vue'
import MaDeviation from './components/MaDeviation.vue'
import TrendKing from './components/TrendKing.vue'
import WindowRanking from './components/WindowRanking.vue'
import IndustryMarket from './components/IndustryMarket.vue'
import IndustryTrend from './components/IndustryTrend.vue'
import DataConfig from './components/DataConfig.vue'
import StockTrend from './components/StockTrend.vue'
import IndexTrend from './components/IndexTrend.vue'
import SimTrading from './components/SimTrading.vue'
import ThemeTagging from './components/ThemeTagging.vue'
import ThemeDict from './components/ThemeDict.vue'
import UserManagement from './components/UserManagement.vue'
import LoginHistory from './components/LoginHistory.vue'
import SyncLog from './components/SyncLog.vue'
import Login from './components/Login.vue'
import DesktopLayout from './layouts/DesktopLayout.vue'
import MobileLayout from './layouts/MobileLayout.vue'
import MobileHome from './mobile/MobileHome.vue'
import MobileRecommend from './mobile/MobileRecommend.vue'
import MobileRadar from './mobile/MobileRadar.vue'
import MobileKline from './mobile/MobileKline.vue'
import MobileFund from './mobile/MobileFund.vue'
import MobileMore from './mobile/MobileMore.vue'
import MobileSimTrading from './mobile/MobileSimTrading.vue'
import MobileTD9 from './mobile/MobileTD9.vue'
import MobileTrendKing from './mobile/MobileTrendKing.vue'
import MobileMaDeviation from './mobile/MobileMaDeviation.vue'
import { fetchCurrentUser, logout } from './utils/authApi.js'
import { setUnauthorizedHandler } from './utils/http.js'
import { createIdleTimer, DEFAULT_IDLE_MS, DEFAULT_AWAY_MS } from './utils/idleTimer.js'
import { detectIsMobile, watchIsMobile } from './utils/device.js'

const PAGE_TITLES = {
  home: '首页',
  fund: '基金重仓',
  'fund-industry': '重仓行业',
  'fund-industry-detail': '基金重仓行业明细',
  kline: '日K查询',
  'system-recommend': '系统推荐',
  backtest: '策略回测',
  'backtest-forecast': '策略预测',
  'trend-king': '趋势为王',
  td9: '神奇九转',
  'ma-deviation': '年线偏离',
  'sim-trading': '模拟实盘',
  'window-ranking': '波段雷达',
  more: '更多',
  industry: '行业行情',
  'industry-trend': '行业趋势',
  'data-config': '数据配置',
  'stock-trend': '个股趋势',
  'index-trend': '指数趋势',
  'theme-tagging': '题材打标',
  'theme-dict': '题材字典',
  users: '用户管理',
  'login-logs': '登录历史',
  'sync-logs': '同步日志',
}

const ADMIN_MENUS = new Set(['users', 'login-logs', 'sync-logs', 'theme-tagging', 'theme-dict'])
// 底部 tabbar 的一级入口（「策略回测 / 策略预测」收进「更多」；进入这些页时 tabbar 高亮「更多」）
const MOBILE_TAB_MENUS = new Set(['home', 'system-recommend', 'window-ranking', 'trend-king', 'more'])
// PC 页在移动端直接复用的二级页（顶部带「移动端为简化入口」提示）
const MOBILE_EXTRA_MENUS = new Set([
  'industry', 'industry-trend', 'stock-trend', 'index-trend', 'fund', 'fund-industry',
  'fund-industry-detail', 'kline',
])
// 全部二级页（含移动端原生实现的「模拟实盘」「神奇九转」「年线偏离」）：返回键统一回「更多」
const MOBILE_SUB_PAGES = new Set([...MOBILE_EXTRA_MENUS, 'sim-trading', 'td9', 'backtest-forecast', 'ma-deviation'])
const DEFAULT_MENU = 'home'

function parseHash() {
  const raw = window.location.hash.replace(/^#\/?/, '').trim()
  const qIdx = raw.indexOf('?')
  const path = qIdx >= 0 ? raw.slice(0, qIdx) : raw
  const query = {}
  if (qIdx >= 0) {
    const params = new URLSearchParams(raw.slice(qIdx + 1))
    params.forEach((value, key) => {
      query[key] = value
    })
  }
  const menu = PAGE_TITLES[path] ? path : DEFAULT_MENU
  return { menu, query }
}

function parseMenuFromHash() {
  return parseHash().menu
}

function setMenuHash(menu, replace = false) {
  const hash = `#/${menu}`
  if (window.location.hash === hash) return
  if (replace) {
    window.history.replaceState(null, '', hash)
  } else {
    window.location.hash = hash
  }
}

export default {
  name: 'App',
  components: {
    HomePage,
    FundHoldings,
    FundIndustry,
    FundIndustryDetail,
    KlineQuery,
    SystemRecommend,
    BacktestView,
    BacktestForecast,
    MagicNineTurn,
    MaDeviation,
    TrendKing,
    WindowRanking,
    IndustryMarket,
    IndustryTrend,
    DataConfig,
    StockTrend,
    IndexTrend,
    SimTrading,
    ThemeTagging,
    ThemeDict,
    UserManagement,
    LoginHistory,
    SyncLog,
    Login,
    DesktopLayout,
    MobileLayout,
    MobileHome,
    MobileRecommend,
    MobileRadar,
    MobileKline,
    MobileFund,
    MobileMore,
    MobileSimTrading,
    MobileTD9,
    MobileTrendKing,
    MobileMaDeviation,
  },
  data() {
    return {
      authLoading: true,
      user: null,
      idleMinutes: 5,
      awayMinutes: 30,
      activeMenu: parseMenuFromHash(),
      routeQuery: parseHash().query,
      idleTimer: null,
      sessionHeartbeat: null,
      isMobile: detectIsMobile(),
      stopWatchMobile: null,
      loadedPages: {
        home: false,
        fund: false,
        'fund-industry': false,
        'fund-industry-detail': false,
        kline: false,
        'system-recommend': false,
        backtest: false,
        'backtest-forecast': false,
        'trend-king': false,
        td9: false,
        'ma-deviation': false,
        'sim-trading': false,
        'window-ranking': false,
        more: false,
        industry: false,
        'industry-trend': false,
        'data-config': false,
        'stock-trend': false,
        'index-trend': false,
        'theme-tagging': false,
        'theme-dict': false,
        users: false,
        'login-logs': false,
        'sync-logs': false,
      },
    }
  },
  computed: {
    isAdmin() {
      return !!this.user?.isAdmin
    },
    pageTitle() {
      return PAGE_TITLES[this.activeMenu] || '投研系统'
    },
    mobileActiveMenu() {
      return MOBILE_TAB_MENUS.has(this.activeMenu) ? this.activeMenu : 'more'
    },
    isMobileExtraPage() {
      return this.isMobile && MOBILE_EXTRA_MENUS.has(this.activeMenu)
    },
  },
  created() {
    setUnauthorizedHandler(() => {
      if (!this.user) return
      this.forceLogout('登录已过期，请重新登录')
    })
    this.syncMenuFromHash()
    window.addEventListener('hashchange', this.onHashChange)
    this.stopWatchMobile = watchIsMobile((val) => {
      this.isMobile = val
      this.ensureAccessibleMenu()
    })
    this.bootstrapAuth()
  },
  beforeDestroy() {
    window.removeEventListener('hashchange', this.onHashChange)
    if (this.stopWatchMobile) this.stopWatchMobile()
    this.stopIdleTimer()
    this.stopSessionHeartbeat()
  },
  methods: {
    async bootstrapAuth() {
      this.authLoading = true
      try {
        const data = await fetchCurrentUser()
        if (data?.user) {
          this.onLoginSuccess(data, { silent: true })
        } else {
          this.user = null
          this.stopIdleTimer()
          this.stopSessionHeartbeat()
        }
      } finally {
        this.authLoading = false
      }
    },
    onLoginSuccess(data, { silent = false } = {}) {
      if (!silent) {
        this.activeMenu = DEFAULT_MENU
        setMenuHash(DEFAULT_MENU, true)
      }
      this.user = data.user
      this.idleMinutes = data.idleMinutes || 5
      this.awayMinutes = data.awayMinutes || 30
      this.ensureAccessibleMenu()
      this.markPageLoaded(this.activeMenu)
      this.startIdleTimer()
      this.startSessionHeartbeat()
      if (!silent) {
        this.$message.success(`欢迎，${this.user.username}`)
      }
    },
    startIdleTimer() {
      this.stopIdleTimer()
      const timeoutMs = (this.idleMinutes || 5) * 60 * 1000 || DEFAULT_IDLE_MS
      const awayTimeoutMs = (this.awayMinutes || 30) * 60 * 1000 || DEFAULT_AWAY_MS
      this.idleTimer = createIdleTimer({
        timeoutMs,
        awayTimeoutMs,
        onIdle: (reason) => {
          const message = reason === 'away'
            ? `${this.awayMinutes} 分钟未回到本页，已自动退出`
            : `${this.idleMinutes} 分钟无操作，已自动退出`
          this.forceLogout(message)
        },
      })
      this.idleTimer.start()
    },
    stopIdleTimer() {
      if (this.idleTimer) {
        this.idleTimer.stop()
        this.idleTimer = null
      }
    },
    startSessionHeartbeat() {
      this.stopSessionHeartbeat()
      const beat = async () => {
        if (!this.user || document.hidden) return
        try {
          const data = await fetchCurrentUser()
          if (!data?.user) {
            this.forceLogout('登录已过期，请重新登录')
          }
        } catch {
          // ignore
        }
      }
      this.sessionHeartbeat = setInterval(beat, 2 * 60 * 1000)
    },
    stopSessionHeartbeat() {
      if (this.sessionHeartbeat) {
        clearInterval(this.sessionHeartbeat)
        this.sessionHeartbeat = null
      }
    },
    async forceLogout(message) {
      this.stopIdleTimer()
      this.stopSessionHeartbeat()
      this.user = null
      this.activeMenu = DEFAULT_MENU
      setMenuHash(DEFAULT_MENU, true)
      await logout()
      if (message) {
        this.$message.warning(message)
      }
    },
    async handleLogout() {
      await this.forceLogout()
      this.$message.success('已退出登录')
    },
    onMenuSelect(index) {
      if (ADMIN_MENUS.has(index) && !this.isAdmin) return
      if (this.isMobile && (index === 'data-config' || ADMIN_MENUS.has(index))) {
        this.$message.info('该功能请使用电脑版')
        return
      }
      this.activeMenu = index
      setMenuHash(index)
      requestAnimationFrame(() => {
        this.markPageLoaded(index)
      })
      if (this.idleTimer) this.idleTimer.reset()
    },
    onMobileBack() {
      // 行业基金明细 → 返回重仓行业
      if (this.activeMenu === 'fund-industry-detail') {
        this.onMenuSelect('fund-industry')
        return
      }
      // 二级功能页（含策略回测、模拟实盘）→ 返回到「更多」
      if (MOBILE_SUB_PAGES.has(this.activeMenu) || this.activeMenu === 'backtest') {
        this.onMenuSelect('more')
        return
      }
      if (this.activeMenu !== DEFAULT_MENU) {
        this.onMenuSelect(DEFAULT_MENU)
      }
    },
    onHashChange() {
      const { menu, query } = parseHash()
      if (ADMIN_MENUS.has(menu) && (!this.isAdmin || this.isMobile)) {
        this.activeMenu = DEFAULT_MENU
        this.routeQuery = {}
        setMenuHash(DEFAULT_MENU, true)
        return
      }
      this.activeMenu = menu
      this.routeQuery = query
      this.markPageLoaded(menu)
    },
    syncMenuFromHash() {
      const { menu, query } = parseHash()
      this.activeMenu = menu
      this.routeQuery = query
      this.markPageLoaded(menu)
      // 保留 query（如明细页 period/code），避免 replace 把参数清掉
      const qs = Object.keys(query).length
        ? `?${new URLSearchParams(query).toString()}`
        : ''
      const hash = `#/${menu}${qs}`
      if (window.location.hash !== hash) {
        window.history.replaceState(null, '', hash)
      }
    },
    markPageLoaded(menu) {
      if (menu in this.loadedPages) {
        this.loadedPages[menu] = true
      }
    },
    ensureAccessibleMenu() {
      // admin 页组件只挂在 DesktopLayout 下：非 admin 或移动端 hash 直达都回首页
      if ((!this.isAdmin || this.isMobile) && ADMIN_MENUS.has(this.activeMenu)) {
        this.activeMenu = DEFAULT_MENU
        setMenuHash(DEFAULT_MENU, true)
      }
    },
  },
}
</script>

<style>
html,
body,
#app {
  height: 100%;
  width: 100%;
  margin: 0;
}

#app {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #2c3e50;
  overflow-x: hidden;
}

#app.is-mobile {
  font-family: -apple-system, BlinkMacSystemFont, 'PingFang SC', 'Helvetica Neue', Arial, sans-serif;
}

.auth-loading {
  min-height: 100vh;
  width: 100%;
}

.page-shell {
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
}

.table-shell {
  width: 100%;
  min-width: 0;
  min-height: 560px;
  box-sizing: border-box;
}

.table-shell >>> .el-table {
  width: 100% !important;
}

.m-extra-wrap {
  padding-bottom: 8px;
}

.m-extra-wrap .page-shell {
  padding: 8px 12px;
}
</style>
